import { useCallback, useMemo, useState } from 'react';
import {
  useTriggerExtraction,
  useUpdateConnector,
  useDeleteConnector,
} from '@/hooks/useConnectors';
import type { Connector } from '@/types/connectors';
import type { BulkRowStatus } from '@/components/connectors/library/BulkRowStatusBadge';

export type RowStatusMap = Record<string, BulkRowStatus>;

export interface BulkActionResult {
  succeeded: Connector[];
  failed: Array<{ connector: Connector; error: string }>;
}

export interface UseBulkConnectorActionsResult {
  rowStatus: RowStatusMap;
  runBulk: (connectors: Connector[]) => Promise<BulkActionResult>;
  disableBulk: (connectors: Connector[]) => Promise<BulkActionResult>;
  deleteBulk: (connectors: Connector[]) => Promise<BulkActionResult>;
  clearStatus: () => void;
  isAnyPending: boolean;
}

function rowIdOf(c: Connector): string {
  return String(c.id ?? c.connectorType);
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}

export function useBulkConnectorActions(): UseBulkConnectorActionsResult {
  const trigger = useTriggerExtraction();
  const update = useUpdateConnector();
  const del = useDeleteConnector();

  const [rowStatus, setRowStatus] = useState<RowStatusMap>({});

  const isAnyPending = useMemo(
    () => Object.values(rowStatus).some((s) => s === 'pending'),
    [rowStatus]
  );

  const markPending = useCallback((ids: string[]) => {
    setRowStatus((prev) => {
      const next = { ...prev };
      for (const id of ids) next[id] = 'pending';
      return next;
    });
  }, []);

  const markResults = useCallback(
    (succeededIds: string[], failedIds: string[]) => {
      setRowStatus((prev) => {
        const next = { ...prev };
        for (const id of succeededIds) next[id] = 'success';
        for (const id of failedIds) next[id] = 'failure';
        return next;
      });
    },
    []
  );

  async function dispatchPerRow(
    connectors: Connector[],
    apply: (c: Connector) => Promise<unknown>
  ): Promise<BulkActionResult> {
    markPending(connectors.map(rowIdOf));
    const settled = await Promise.allSettled(connectors.map(apply));
    const succeeded: Connector[] = [];
    const failed: Array<{ connector: Connector; error: string }> = [];
    settled.forEach((res, i) => {
      const c = connectors[i];
      if (res.status === 'fulfilled') succeeded.push(c);
      else failed.push({ connector: c, error: errorMessage(res.reason) });
    });
    markResults(succeeded.map(rowIdOf), failed.map((f) => rowIdOf(f.connector)));
    return { succeeded, failed };
  }

  const runBulk = useCallback(
    (connectors: Connector[]) =>
      dispatchPerRow(connectors, (c) =>
        trigger.mutateAsync({
          connectorType: c.connectorType,
          configurationId: c.id ? String(c.id) : undefined,
        })
      ),
    // We intentionally don't depend on the mutation instance; the call site is closure-fresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [markPending, markResults]
  );

  const disableBulk = useCallback(
    async (connectors: Connector[]): Promise<BulkActionResult> => {
      const toUpdate = connectors.filter((c) => c.enabled !== false);
      const toSkip = connectors.filter((c) => c.enabled === false);

      // Skips are immediate successes; mark and no API call.
      if (toSkip.length) markResults(toSkip.map(rowIdOf), []);

      const dispatched = await dispatchPerRow(toUpdate, (c) =>
        update.mutateAsync({
          connectorType: c.connectorType,
          name: c.name,
          config: { enabled: false },
        })
      );
      return {
        succeeded: [...dispatched.succeeded, ...toSkip],
        failed: dispatched.failed,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [markResults, markPending]
  );

  const deleteBulk = useCallback(
    (connectors: Connector[]) =>
      dispatchPerRow(connectors, async (c) => {
        if (!c.id) throw new Error('Connector has no id');
        return del.mutateAsync({ configId: c.id });
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [markPending, markResults]
  );

  const clearStatus = useCallback(() => setRowStatus({}), []);

  return { rowStatus, runBulk, disableBulk, deleteBulk, clearStatus, isAnyPending };
}
